// hooks/useNativeFeatures.js
// Wrapper around Capacitor APIs that gracefully degrades to web equivalents.
// Use this everywhere instead of importing Capacitor plugins directly,
// so the same code works in browser AND native app.

import { useEffect, useState } from 'react';

// Detect runtime - true if running inside Capacitor native shell
export const isNative = () => {
  return typeof window !== 'undefined' &&
    window.Capacitor !== undefined &&
    window.Capacitor.isNativePlatform &&
    window.Capacitor.isNativePlatform();
};

export const getPlatform = () => {
  if (!isNative()) return 'web';
  return window.Capacitor.getPlatform(); // 'ios' | 'android'
};

// ── GEOLOCATION ──
// Drop-in replacement for navigator.geolocation that works in native shell
// Falls back to browser API on web
export const getCurrentPosition = async (options = {}) => {
  if (isNative()) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const result = await Geolocation.getCurrentPosition({
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        timeout: options.timeout ?? 10000,
        maximumAge: options.maximumAge ?? 0,
      });
      return {
        coords: {
          latitude: result.coords.latitude,
          longitude: result.coords.longitude,
          accuracy: result.coords.accuracy,
        },
      };
    } catch (err) {
      throw new Error(err.message || 'Could not get location');
    }
  }

  // Web fallback
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
};

// Check geolocation permission state
export const checkLocationPermission = async () => {
  if (isNative()) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const status = await Geolocation.checkPermissions();
      return status.location; // 'granted' | 'denied' | 'prompt'
    } catch {
      return 'prompt';
    }
  }

  if (navigator.permissions?.query) {
    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state; // 'granted' | 'denied' | 'prompt'
    } catch {
      return 'prompt';
    }
  }

  return 'prompt';
};

export const requestLocationPermission = async () => {
  if (isNative()) {
    try {
      const { Geolocation } = await import('@capacitor/geolocation');
      const result = await Geolocation.requestPermissions();
      return result.location;
    } catch {
      return 'denied';
    }
  }

  // Web has no explicit request - calling getCurrentPosition triggers it
  try {
    await getCurrentPosition({ timeout: 1000 });
    return 'granted';
  } catch {
    return 'denied';
  }
};

// ── PUSH NOTIFICATIONS ──
let pushInitialized = false;

export const initPushNotifications = async (onTokenReceived, onNotificationReceived) => {
  if (!isNative() || pushInitialized) return; // No web push in this MVP

  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');

    // Never interrupt first launch with an unexplained system prompt. A
    // settings/education surface can request permission intentionally; app
    // bootstrap only registers devices that have already granted it.
    const permResult = await PushNotifications.checkPermissions();
    if (permResult.receive !== 'granted') {
      return;
    }

    pushInitialized = true;

    // Register with FCM/APNS
    await PushNotifications.register();

    // Token received from FCM/APNS - send this to your backend
    PushNotifications.addListener('registration', (token) => {
      if (onTokenReceived) onTokenReceived(token.value);
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error:', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      if (onNotificationReceived) onNotificationReceived(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      // Navigate to relevant screen based on notification data
      const notificationData = action.notification?.data;
      // Only follow same-origin targets - never let a push payload navigate
      // the native webview to an arbitrary external URL.
      if (notificationData?.url && window.location) {
        try {
          const dest = new URL(notificationData.url, window.location.origin);
          if (dest.origin === window.location.origin) {
            window.location.href = dest.pathname + dest.search + dest.hash;
          }
        } catch { /* malformed url - ignore */ }
      }
    });
  } catch (err) {
    console.error('Push init error:', err);
  }
};

// ── STATUS BAR ──
export const setStatusBarStyle = async (style = 'dark') => {
  if (!isNative()) return;
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: style === 'dark' ? Style.Dark : Style.Light });
  } catch {}
};

// ── HAPTICS (subtle feedback on button taps) ──
export const triggerHaptic = async (type = 'light') => {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    const style = type === 'heavy' ? ImpactStyle.Heavy
                : type === 'medium' ? ImpactStyle.Medium
                : ImpactStyle.Light;
    await Haptics.impact({ style });
  } catch {}
};

// ── APP STATE (handle background/foreground) ──
export const useAppState = () => {
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (!isNative()) return;

    let removeListener;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appStateChange', ({ isActive: active }) => {
          setIsActive(active);
        });
        removeListener = () => handle.remove();
      } catch {}
    })();

    return () => { if (removeListener) removeListener(); };
  }, []);

  return isActive;
};

// ── DEEP LINK / URL OPEN HANDLING ──
// Listen for when user opens app via a custom URL or universal link
export const useAppUrlOpen = (handler) => {
  useEffect(() => {
    if (!isNative()) return;

    let removeListener;
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appUrlOpen', (event) => {
          if (handler) handler(event.url);
        });
        removeListener = () => handle.remove();
      } catch {}
    })();

    return () => { if (removeListener) removeListener(); };
  }, [handler]);
};
